import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner, Card } from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authService, config } from '../services/api';
import './EventReservation.css';

function EventReservation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const [user, setUser] = useState(authService.getCurrentUser() || {});
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [paymentProof, setPaymentProof] = useState(null);
  // Update the initial state to include reserved_seats
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    reserved_seats: 1,
    medical_information: ''
  });

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/reservation/${id}` } });
      return;
    }

    // Fetch event details
    const fetchEventDetail = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/api/events/${id}/`);
        setEvent(response.data);
       // user = authService.getCurrentUser();
        // Pre-fill form with user data if available
        if (user) {
          setFormData(prevState => ({
            ...prevState,
            name: user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.full_name || '',
            email: user.email || '',
            phone: user.phone || ''
          }));
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching event details:', error);
        setError('Impossible de charger les détails de l\'événement. Veuillez réessayer plus tard.');
        setLoading(false);
      }
    };

    fetchEventDetail();
  }, [id, isAuthenticated, navigate, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    setPaymentProof(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      
      // Validate phone number format
      const phoneRegex = /^[0-9+\-\s()]*$/;
      if (!phoneRegex.test(formData.phone)) {
        setError('Le numéro de téléphone n\'est pas valide. Veuillez entrer un format correct.');
        setSubmitting(false);
        return;
      }
      
      const reservationData = new FormData();
      reservationData.append('event', id);
      reservationData.append('event_id', id);
      
      // Don't need to send user ID as the backend will use the authenticated user
      // reservationData.append('user', user.id);
      
      reservationData.append('name', formData.name);
      reservationData.append('email', formData.email);
      reservationData.append('phone', formData.phone);
      reservationData.append('reserved_seats', formData.reserved_seats || 1);
      reservationData.append('notes', formData.notes || '');
      
      if (formData.medical_information) {
        reservationData.append('medical_information', formData.medical_information);
      }
      
      if (paymentProof) {
        reservationData.append('payment_proof', paymentProof);
      }
      
      // Debug what we're sending
      console.log('Sending reservation data:', Object.fromEntries(reservationData));
      
      const response = await axios.post(
        `${config.API_URL}/api/reservations/`, 
        reservationData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      setSuccess(true);
      setSubmitting(false);
      
      // Redirect to confirmation page after a short delay
      setTimeout(() => {
        navigate(`/reservation-confirmation/${response.data.id}`);
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting reservation:', error);
      
      // More detailed error handling
      if (error.response) {
        console.error('Error response:', error.response);
        
        if (error.response.data) {
          // Handle Django REST Framework validation errors
          if (typeof error.response.data === 'object') {
            const errorMessages = [];
            
            // Extract error messages from the response
            Object.entries(error.response.data).forEach(([field, errors]) => {
              if (Array.isArray(errors)) {
                errorMessages.push(`${field}: ${errors.join(', ')}`);
              } else if (typeof errors === 'string') {
                errorMessages.push(`${field}: ${errors}`);
              }
            });
            
            if (errorMessages.length > 0) {
              setError(errorMessages.join('\n'));
            } else {
              setError(JSON.stringify(error.response.data));
            }
          } else {
            setError(error.response.data);
          }
        } else {
          setError(`Erreur ${error.response.status}: ${error.response.statusText}`);
        }
      } else {
        setError('Une erreur est survenue lors de la réservation. Veuillez réessayer.');
      }
      
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center my-5">
          <Spinner animation="border" variant="danger" />
          <p className="mt-3">Chargement des détails de l'événement...</p>
        </div>
      </Container>
    );
  }

  if (error && !event) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </Alert>
        <div className="text-center mt-4">
          <Link to="/eventlist" className="btn btn-outline-danger">
            <i className="fas fa-arrow-left me-2"></i>Retour aux événements
          </Link>
        </div>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Événement non trouvé
        </Alert>
        <div className="text-center mt-4">
          <Link to="/eventlist" className="btn btn-outline-danger">
            <i className="fas fa-arrow-left me-2"></i>Retour aux événements
          </Link>
        </div>
      </Container>
    );
  }

  if (success) {
    return (
      <Container className="py-5">
        <Alert variant="success">
          <i className="fas fa-check-circle me-2"></i>
          Votre réservation a été enregistrée avec succès! Redirection en cours...
        </Alert>
      </Container>
    );
  }

  return (
    <div className="reservation-page py-5">
      <Container>
        <Row>
          <Col lg={8} className="mx-auto">
            <Card className="shadow-sm">
              <Card.Header className="bg-danger text-white py-3">
                <h2 className="mb-0 fs-4">Réservation pour {event.title}</h2>
              </Card.Header>
              
              <Card.Body className="p-4">
                {/* Event Summary */}
                <div className="event-summary mb-4 p-3 bg-light rounded">
                  <Row>
                    <Col md={3}>
                      {event.image && (
                        <img 
                          src={event.image} 
                          alt={event.title} 
                          className="img-fluid rounded"
                        />
                      )}
                    </Col>
                    <Col md={9}>
                      <h5>{event.title}</h5>
                      <div className="event-meta text-muted small">
                        <div><i className="far fa-calendar-alt me-2"></i>{event.formatted_date}</div>
                        <div><i className="far fa-clock me-2"></i>{event.time}</div>
                        <div><i className="fas fa-map-marker-alt me-2"></i>{event.location}</div>
                        {event.price && (
                          <div><i className="fas fa-ticket-alt me-2"></i>{event.price} DZD</div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Reservation Form */}
                {error && (
                  <Alert variant="danger" className="mb-4">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <h4 className="mb-3">Informations personnelles</h4>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Nom complet</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      //disabled
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Téléphone</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="Ex: 0123456789"
                      pattern="[0-9+\-\s()]+"
                    />
                    <Form.Text className="text-muted">
                      Entrez un numéro de téléphone valide
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label>Réservation</Form.Label>
                    <Form.Control
                      type="number"
                      name="reserved_seats"
                      value={formData.reserved_seats}
                      onChange={handleInputChange}
                      min="1"
                      max={event.available_seats || 10}
                      required
                    />
                    {event.available_seats && (
                      <Form.Text className="text-muted">
                        Places disponibles: {event.available_seats}
                      </Form.Text>
                    )}
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label>Informations médicales (optionnel)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="medical_information"
                      value={formData.medical_information}
                      onChange={handleInputChange}
                      placeholder="Allergies, conditions médicales, ou besoins spécifiques"
                    />
                  </Form.Group>
                  
                  {event.price > 0 && (
                    <>
                      <h4 className="mb-3 mt-4">Informations de paiement</h4>
                      
                      <div className="payment-info bg-light p-3 rounded mb-3">
                        <h5 className="mb-3">Instructions de paiement</h5>
                        <div className="total-amount-display bg-danger text-white p-3 mb-3 rounded text-center">
                          <h4 className="mb-0">Montant total: {formatPrice(event.price * formData.reserved_seats)}</h4>
                        </div>
                        <p>Pour finaliser votre réservation, veuillez effectuer un virement au compte suivant:</p>
                        <ul className="mb-3">
                          <li>Banque: Banque Nationale d'Algérie</li>
                          <li>Numéro de compte: 123456789</li>
                          <li>Bénéficiaire: DeltaX</li>
                          <li>Montant: {event.price * formData.reserved_seats} DZD ({event.price} DZD × {formData.reserved_seats} places)</li>
                        </ul>
                        <p className="mb-0">Après avoir effectué le paiement, veuillez télécharger une preuve de paiement ci-dessous.</p>
                      </div>
                      
                  
                    </>
                  )}
                  
                  <Form.Group className="mb-4">
                    <Form.Label>Notes supplémentaires (optionnel)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                  
                  <div className="d-flex justify-content-between mt-4">
                    <Link to={`/event/${id}`} className="btn btn-outline-secondary">
                      <i className="fas fa-arrow-left me-2"></i>
                      Retour à l'événement
                    </Link>
                    <Button 
                      type="submit" 
                      variant="danger" 
                      size="lg"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Traitement en cours...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle me-2"></i>
                          Confirmer la réservation
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default EventReservation;

// Add this helper function near the top of the component
const formatPrice = (price) => {
  if (!price) return 'Gratuit';
  // Remove decimal places and add thousands separator
  return parseInt(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") + ' DZD';
};

// Then update any price displays to use the formatPrice function